import { motion } from 'framer-motion';
import { displayFont, fadeInUp, staggerContainer } from './shared';
import type { HomepageStrings } from '../../i18n/homepage';

type OurStoryStrings = Pick<
  HomepageStrings,
  | 'our_story_badge'
  | 'our_story_h2_pre'
  | 'our_story_h2_highlight'
  | 'our_story_body'
>;

const EN_DEFAULTS: OurStoryStrings = {
  our_story_badge: 'Our Story',
  our_story_h2_pre: 'Built by a couple, ',
  our_story_h2_highlight: 'for couples',
  our_story_body: "When we started planning our own wedding, we were frustrated by expensive invitation platforms with hidden fees and clunky guest management tools. We wanted something beautiful, simple, and honest — so we built it ourselves. Wedsy is everything we wish we'd had: stunning designs, powerful features, and one fair price. No surprises, no per-guest charges, just the tools you need to celebrate your day your way.",
};

export function OurStorySection({ strings }: { strings?: OurStoryStrings }) {
  const s = strings ?? EN_DEFAULTS;

  return (
    <section className="py-20 md:py-32 bg-background overflow-hidden">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="text-center"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-4"
          >
            {s.our_story_badge}
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl text-text-primary mb-6"
            style={displayFont}
          >
            {s.our_story_h2_pre}<span className="text-primary">{s.our_story_h2_highlight}</span>
          </motion.h2>

          {/* Founders Headshots */}
          <motion.div
            variants={fadeInUp}
            className="flex items-center justify-center gap-6 mb-8"
          >
            <div className="relative">
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-primary/20">
                <img
                  src="/images/founders/kijo.jpg"
                  alt="Kijo"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="text-2xl sm:text-3xl md:text-4xl text-primary" style={displayFont}>&</div>
            <div className="relative">
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-primary/20">
                <img
                  src="/images/founders/sara.jpg"
                  alt="Sara"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </motion.div>

          <motion.p
            variants={fadeInUp}
            className="text-base md:text-lg text-text-secondary max-w-2xl mx-auto leading-relaxed"
          >
            {s.our_story_body}
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}
